import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Cctv, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function SetupPage() {
  const { setupRequired, setup, user, error, setError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("Administrateur");
  const [busy, setBusy] = useState(false);

  if (setupRequired === false) return <Navigate to={user ? "/cameras" : "/login"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError("Mot de passe : 8 caractères minimum."); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    setBusy(true);
    await setup(email, password, name);
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0b0b0d] flex items-center justify-center px-4 py-10" data-testid="setup-page">
      <div className="w-full max-w-sm">
        <div className="mb-7 text-center">
          <div className="mx-auto mb-4 size-11 rounded-2xl bg-brand/15 text-brand grid place-items-center">
            <Cctv className="size-5" strokeWidth={1.8} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-white">Configurer CAM</h1>
          <p className="mt-2 text-sm text-zinc-400">Créez le compte administrateur initial.</p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-5 sm:p-6 space-y-4 shadow-2xl shadow-black/20"
          data-testid="setup-form"
        >
          <Field label="Nom" htmlFor="setup-name">
            <Input
              id="setup-name"
              data-testid="setup-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              className="h-11 rounded-xl border-white/[0.09] bg-black/20 px-3 text-sm text-white focus-visible:ring-brand"
            />
          </Field>

          <Field label="Adresse e-mail" htmlFor="setup-email">
            <Input
              id="setup-email"
              data-testid="setup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="h-11 rounded-xl border-white/[0.09] bg-black/20 px-3 text-sm text-white focus-visible:ring-brand"
            />
          </Field>

          <Field label="Mot de passe" htmlFor="setup-password">
            <Input
              id="setup-password"
              data-testid="setup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="h-11 rounded-xl border-white/[0.09] bg-black/20 px-3 text-sm text-white focus-visible:ring-brand"
            />
            <p className="text-xs text-zinc-600 mt-1.5">8 caractères minimum.</p>
          </Field>

          <Field label="Confirmer le mot de passe" htmlFor="setup-confirm">
            <Input
              id="setup-confirm"
              data-testid="setup-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              className="h-11 rounded-xl border-white/[0.09] bg-black/20 px-3 text-sm text-white focus-visible:ring-brand"
            />
          </Field>

          {error && (
            <div
              className="rounded-xl border border-status-offline/30 bg-status-offline/10 px-3 py-2.5 text-sm text-red-300 flex items-start gap-2"
              data-testid="setup-error"
            >
              <ShieldAlert className="size-4 shrink-0 mt-0.5" strokeWidth={1.8} />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="setup-submit"
            className="w-full h-11 rounded-xl bg-brand hover:bg-brand-hover text-white font-medium"
          >
            {busy ? "Création…" : "Créer le compte"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-zinc-300">{label}</Label>
      {children}
    </div>
  );
}
